import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { styled } from '@mui/material/styles';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { Button } from '@liminal/web-ui/primitives/Button';
import { SearchInput } from '@liminal/web-ui/forms/SearchInput';
import { SegmentedToggle, type SegmentedOption } from '@liminal/web-ui/forms/SegmentedToggle';
import { Modal } from '@liminal/web-ui/overlays/Modal';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { useDebounce } from '@/hooks/useDebounce';
import { useUrlState } from '@/hooks/useUrlState';
import { useFeedback } from '@/hooks/useFeedback';
import { feedbackKindLabel, feedbackKindVariant } from '@/components/ui/statusHelpers';
import { formatOrderDate } from '@liminal/utils/date';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { FeedbackKind } from '@/api/generated';
import type { AdminFeedbackItem } from '@/api/types';

const PAGE_SIZE = 25;
const COLUMN_COUNT = 6;

/**
 * The tab's filter. `all` is the absence of a kind filter rather than a fourth
 * category, so it maps to an empty `kinds` list on the wire.
 */
type KindFilter = 'all' | FeedbackKind;

const KIND_FILTERS: readonly SegmentedOption<KindFilter>[] = [
  { value: 'all', label: 'All' },
  { value: FeedbackKind.FEEDBACK_KIND_BUG, label: 'Bugs' },
  { value: FeedbackKind.FEEDBACK_KIND_FEATURE_REQUEST, label: 'Requests' },
  { value: FeedbackKind.FEEDBACK_KIND_FEEDBACK, label: 'Feedback' },
];

// Details run to several paragraphs, so the cell shows the opening of the text
// and the modal shows all of it.
const DetailsPreview = styled(Typography)({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

function fullName(f: AdminFeedbackItem): string {
  return [f.firstName, f.lastName].filter(Boolean).join(' ');
}

/**
 * Sorts the page on screen — the endpoint returns feedback newest-first and
 * takes no sort parameter, so this reorders the rows already fetched.
 */
const SORT_ACCESSORS = {
  kind: (f: AdminFeedbackItem) => feedbackKindLabel(f.kind),
  title: (f: AdminFeedbackItem) => f.title,
  email: (f: AdminFeedbackItem) => f.email,
  name: (f: AdminFeedbackItem) => fullName(f),
  createdAt: (f: AdminFeedbackItem) => f.createdAt,
};

const FILTER_DEFAULTS = { q: '', kind: 'all' as KindFilter };

function isKindFilter(value: string): value is KindFilter {
  return KIND_FILTERS.some((option) => option.value === value);
}

export function FeedbackPage() {
  const navigate = useNavigate();
  const [{ q: search, kind }, setParams] = useUrlState(FILTER_DEFAULTS);
  const pagination = useCursorPagination(PAGE_SIZE);
  const [selected, setSelected] = useState<AdminFeedbackItem | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  // A URL can carry anything; an unrecognised kind falls back to no filter
  // rather than being sent upstream, where it would be rejected.
  const kindFilter: KindFilter = isKindFilter(kind) ? kind : 'all';

  const { data, isLoading, isError } = useFeedback(
    debouncedSearch,
    kindFilter === 'all' ? [] : [kindFilter],
    PAGE_SIZE,
    pagination.pageToken ?? '',
  );
  const totalCount = data?.totalCount ?? 0;
  const nextPageToken = data?.nextPageToken ?? '';

  const resetPaging = pagination.reset;

  // Newest first, matching the order the endpoint already returns.
  const { rows: feedback, sort } = useTableSort(
    data?.feedback ?? [],
    SORT_ACCESSORS,
    'createdAt',
    'desc',
  );

  return (
    <Stack spacing={3}>
      {/* Filters */}
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        <SearchInput
          value={search}
          onChange={(value) => {
            setParams({ q: value });
            resetPaging();
          }}
          placeholder="Search by email, name, or user ID"
          width={340}
        />
        <SegmentedToggle
          options={KIND_FILTERS}
          value={kindFilter}
          onChange={(value) => {
            setParams({ kind: value });
            resetPaging();
          }}
          size="sm"
        />
        {totalCount > 0 && (
          <Typography variant="body2" color="text.disabled">
            {totalCount === 1 ? '1 submission' : `${totalCount} submissions`}
          </Typography>
        )}
      </Stack>

      {/* Data Table */}
      <Card variant="elevated" padding="none">
        <Table>
          <TableHead>
            <TableRow>
              <SortableTableCell column="kind" sort={sort}>
                Kind
              </SortableTableCell>
              <SortableTableCell column="title" sort={sort}>
                Title
              </SortableTableCell>
              <TableCell>Details</TableCell>
              <SortableTableCell column="email" sort={sort}>
                Reporter
              </SortableTableCell>
              <SortableTableCell column="name" sort={sort}>
                Name
              </SortableTableCell>
              <SortableTableCell column="createdAt" sort={sort}>
                Submitted
              </SortableTableCell>
            </TableRow>
          </TableHead>
          <DataTableBody
            columns={COLUMN_COUNT}
            isLoading={isLoading}
            isError={isError}
            isEmpty={feedback.length === 0}
            loadingRows={8}
            emptyMessage="No feedback found. Try adjusting your search."
            errorMessage="Could not load feedback."
          >
            {feedback.map((f) => (
              <TableRow
                key={f.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => setSelected(f)}
              >
                <TableCell>
                  <StatusPill tone={feedbackKindVariant(f.kind)} label={feedbackKindLabel(f.kind)} size="sm" />
                </TableCell>
                <TableCell>{f.title || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <DetailsPreview variant="body2" color="text.secondary">
                    {f.details || '—'}
                  </DetailsPreview>
                </TableCell>
                <TableCell>{f.email || '—'}</TableCell>
                <TableCell>{fullName(f) || '—'}</TableCell>
                <TableCell>{f.createdAt ? formatOrderDate(f.createdAt) : '—'}</TableCell>
              </TableRow>
            ))}
          </DataTableBody>
        </Table>
      </Card>

      <CursorPagination
        firstRowNumber={pagination.firstRowNumber}
        count={feedback.length}
        hasPreviousPage={pagination.hasPreviousPage}
        hasNextPage={!!nextPageToken}
        onPreviousPage={pagination.goToPreviousPage}
        onNextPage={() => pagination.goToNextPage(nextPageToken)}
        disabled={isLoading}
      />

      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.title || 'Feedback'}
        width="md"
        dismissible
        actions={
          selected?.conversationId ? (
            <Button onClick={() => navigate(`/conversations/${selected.conversationId}`)}>
              Open conversation
            </Button>
          ) : undefined
        }
      >
        {selected && (
          <Stack spacing={2}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {selected.details}
            </Typography>
            <Stack>
              <DetailRow
                label="Kind"
                value={
                  <StatusPill
                    tone={feedbackKindVariant(selected.kind)}
                    label={feedbackKindLabel(selected.kind)}
                  />
                }
              />
              <DetailRow label="Reporter" value={selected.email || '—'} />
              <DetailRow label="Name" value={fullName(selected) || '—'} />
              <DetailRow label="User" value={<UserIdLink userId={selected.userId} full />} />
              <DetailRow label="Platform" value={selected.platform || '—'} />
              <DetailRow
                label="Submitted"
                value={selected.createdAt ? formatOrderDate(selected.createdAt) : '—'}
              />
            </Stack>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
